import { Container, Point } from 'pixi.js';
import { SYMBOL_IMAGE_CLASS } from '../utils/markdown';

const STYLE_ELEMENT_ID = 'magic-html-scroll-view';
const VIEW_CLASS = 'magic-html-scroll-view';

/**
 * Base look of the panel. Sizing lives in the inline transform (see {@link HtmlScrollView.sync}),
 * so this is only typography, spacing and the scrollbar — everything a game may want to restyle
 * is reachable by appending to it through `options.css`.
 */
const BASE_CSS = `
.${VIEW_CLASS} {
    position: fixed;
    left: 0;
    top: 0;
    transform-origin: 0 0;
    display: none;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    padding: 22px 28px 22px 4px;
    /* Fades whatever is cut off at the top and bottom edges, so a part-scrolled line reads as
       "there is more" instead of as a rendering fault. The vertical padding is what keeps the
       first and last lines out of the fade at either end of the scroll. */
    -webkit-mask-image: linear-gradient(
        to bottom,
        transparent 0,
        #000 22px,
        #000 calc(100% - 22px),
        transparent 100%
    );
    mask-image: linear-gradient(
        to bottom,
        transparent 0,
        #000 22px,
        #000 calc(100% - 22px),
        transparent 100%
    );
    touch-action: pan-y;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    text-align: left;
    color: #eaf3e6;
    font-family: Helvetica, Arial, sans-serif;
    font-size: 22px;
    line-height: 1.5;
    z-index: 10;
}
.${VIEW_CLASS}::-webkit-scrollbar {
    width: 12px;
}
.${VIEW_CLASS}::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.35);
    border-radius: 6px;
}
.${VIEW_CLASS}::-webkit-scrollbar-thumb {
    background: #7cc04a;
    border-radius: 6px;
}
.${VIEW_CLASS} > :first-child {
    margin-top: 0;
}
.${VIEW_CLASS} > :last-child {
    margin-bottom: 0;
}
.${VIEW_CLASS} h1,
.${VIEW_CLASS} h2,
.${VIEW_CLASS} h3,
.${VIEW_CLASS} h4 {
    margin: 1.4em 0 0.5em;
    font-family: Saranai, Helvetica, Arial, sans-serif;
    font-weight: normal;
    line-height: 1.2;
    color: #b6e86a;
    text-transform: uppercase;
}
.${VIEW_CLASS} h1 { font-size: 1.7em; }
.${VIEW_CLASS} h2 { font-size: 1.35em; }
.${VIEW_CLASS} h3 { font-size: 1.1em; }
.${VIEW_CLASS} h4 { font-size: 1em; }
.${VIEW_CLASS} p {
    margin: 0.7em 0;
}
.${VIEW_CLASS} ul,
.${VIEW_CLASS} ol {
    margin: 0.7em 0;
    padding-left: 1.4em;
}
.${VIEW_CLASS} li {
    margin: 0.35em 0;
}
.${VIEW_CLASS} li::marker {
    color: #b6e86a;
}
.${VIEW_CLASS} strong {
    color: #ffffff;
}
.${VIEW_CLASS} a {
    color: #b6e86a;
}
.${VIEW_CLASS} code {
    padding: 0.05em 0.4em;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.4);
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 0.9em;
    white-space: nowrap;
}
.${VIEW_CLASS} pre {
    margin: 0.7em 0;
    padding: 0.8em 1em;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.4);
    overflow-x: auto;
}
.${VIEW_CLASS} pre code {
    padding: 0;
    background: none;
    white-space: pre;
}
.${VIEW_CLASS} blockquote {
    margin: 0.9em 0;
    padding: 0.1em 0 0.1em 1em;
    border-left: 4px solid rgba(182, 232, 106, 0.5);
    color: #c2cfbc;
}
.${VIEW_CLASS} hr {
    margin: 1.2em 0;
    border: 0;
    border-top: 1px solid rgba(234, 243, 230, 0.25);
}
.${VIEW_CLASS} table {
    width: 100%;
    margin: 0.9em 0;
    border-collapse: collapse;
}
.${VIEW_CLASS} th,
.${VIEW_CLASS} td {
    padding: 0.45em 0.6em;
    border-bottom: 1px solid rgba(234, 243, 230, 0.18);
}
.${VIEW_CLASS} th {
    color: #b6e86a;
    font-weight: normal;
    text-align: left;
    text-transform: uppercase;
}
.${VIEW_CLASS} img {
    max-width: 100%;
}
/* A symbol the page named in text, rendered from the game's own art. Every one of them is drawn
   into the same box, so this one height sets them all — and the negative block margins keep a
   symbol from pushing its line taller than the ones around it: it overhangs into the leading,
   which is where the transparent part of the box sits anyway. */
.${VIEW_CLASS} img.${SYMBOL_IMAGE_CLASS} {
    height: 2.4em;
    width: auto;
    margin: -0.55em 0.15em;
    vertical-align: middle;
}
`;

export type HtmlScrollViewOptions = {
    /**
     * Size of the viewport in the container's own units — the box the HTML lays out in, and
     * what {@link sync} projects to the screen. Authoring against a fixed design size is what
     * keeps one set of font sizes correct at every scale the game runs at.
     */
    width: number;
    height: number;
    /** Appended after {@link BASE_CSS}, for per-game theming. Scope selectors under `.magic-html-scroll-view`. */
    css?: string;
};

/**
 * A scrolling HTML panel that tracks a point in the Pixi scene.
 *
 * The content is a real DOM element over the canvas rather than a rasterised `HTMLText`,
 * because it has to scroll: native overflow brings touch momentum, a scrollbar and unbounded
 * content height, none of which a texture-backed text object has (and a tall page would blow
 * past the max texture size long before it ran out of words).
 *
 * The container this extends is what ties the panel to the scene. Add it wherever the content
 * belongs — for a Spine popup, inside the object attached to its content slot — and {@link sync}
 * projects two opposite corners of the viewport box to the screen each frame, so the panel
 * inherits that parent's position, scale and flip without knowing anything about the layout.
 */
export class HtmlScrollView extends Container {
    readonly element: HTMLDivElement;

    #width: number;
    #height: number;
    #open = false;
    /** Set by `open`, cleared by the first `sync` that puts the panel back in the page. */
    #scrollToTop = false;
    #canvas: HTMLCanvasElement | null = null;
    #canvasOffset = new Point();
    #canvasOffsetDirty = true;
    /** The viewport box's corners, and where they project to. Reused so `sync` doesn't allocate. */
    #box = { start: new Point(), end: new Point() };
    #projected = { start: new Point(), end: new Point() };
    #onWindowResize = () => {
        this.#canvasOffsetDirty = true;
    };

    constructor(options: HtmlScrollViewOptions) {
        super();

        this.#width = options.width;
        this.#height = options.height;
        this.#box.start.set(-this.#width / 2, -this.#height / 2);
        this.#box.end.set(this.#width / 2, this.#height / 2);

        injectStyles(options.css);

        this.element = document.createElement('div');
        this.element.className = VIEW_CLASS;
        this.element.style.width = `${this.#width}px`;
        this.element.style.height = `${this.#height}px`;
        document.body.appendChild(this.element);

        window.addEventListener('resize', this.#onWindowResize);
        window.addEventListener('orientationchange', this.#onWindowResize);
    }

    /** The rendered HTML fragment. Setting it scrolls back to the top. */
    set html(html: string) {
        this.element.innerHTML = html;
        this.#scrollToTop = true;
    }

    get html(): string {
        return this.element.innerHTML;
    }

    get isOpen(): boolean {
        return this.#open;
    }

    /** Shows the panel, scrolled to the top. It only appears once {@link sync} has placed it. */
    open() {
        this.#open = true;
        // deferred: an element with `display: none` has no scroll box, so setting scrollTop here
        // would be dropped and the browser would restore the last position when it reappeared
        this.#scrollToTop = true;
    }

    close() {
        this.#open = false;
        this.element.style.display = 'none';
    }

    /**
     * Projects the viewport box onto the screen and moves the panel there.
     *
     * Both opposite corners are projected rather than the origin plus a scale factor, so a
     * parent that flips or mirrors an axis — which a Spine slot object does to Y — still lands
     * the right way up. Call every frame while open: the popup that carries it animates.
     *
     * @param opacity what the panel is drawn at, normally the popup's current alpha so it
     *   fades with it. At zero the panel is taken out of the page, which also stops it
     *   swallowing pointer events meant for the game behind it.
     */
    sync(opacity = 1) {
        if (!this.#open) return;

        if (opacity <= 0) {
            this.element.style.display = 'none';
            return;
        }

        const start = this.toGlobal(this.#box.start, this.#projected.start);
        const end = this.toGlobal(this.#box.end, this.#projected.end);

        const offset = this.#canvasOffsetPosition();
        const left = Math.min(start.x, end.x) + offset.x;
        const top = Math.min(start.y, end.y) + offset.y;
        const scaleX = Math.abs(end.x - start.x) / this.#width || 1;
        const scaleY = Math.abs(end.y - start.y) / this.#height || 1;

        this.element.style.display = 'block';
        this.element.style.opacity = `${opacity}`;
        this.element.style.transform = `translate(${left}px, ${top}px) scale(${scaleX}, ${scaleY})`;

        if (this.#scrollToTop) {
            // now that the panel is in the page it has a scroll box to move
            this.element.scrollTop = 0;
            this.#scrollToTop = false;
        }
    }

    /**
     * Where the canvas sits in the viewport, which is what turns a stage coordinate into the
     * `position: fixed` coordinate the panel is placed at. Read once per resize — every frame
     * would be a layout read for a value that only moves when the window does.
     */
    #canvasOffsetPosition(): Point {
        if (this.#canvasOffsetDirty) {
            this.#canvas ??=
                document.querySelector<HTMLCanvasElement>('#pixi-container canvas') ??
                document.querySelector<HTMLCanvasElement>('canvas');

            const rect = this.#canvas?.getBoundingClientRect();
            this.#canvasOffset.set(rect?.left ?? 0, rect?.top ?? 0);
            this.#canvasOffsetDirty = false;
        }

        return this.#canvasOffset;
    }

    override destroy(...args: Parameters<Container['destroy']>) {
        window.removeEventListener('resize', this.#onWindowResize);
        window.removeEventListener('orientationchange', this.#onWindowResize);
        this.element.remove();
        super.destroy(...args);
    }
}

function injectStyles(css?: string) {
    const id = css ? `${STYLE_ELEMENT_ID}-${hash(css)}` : STYLE_ELEMENT_ID;
    if (document.getElementById(id)) return;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = css ? `${BASE_CSS}\n${css}` : BASE_CSS;
    document.head.appendChild(style);
}

/** Enough to tell one game's stylesheet from another's, so each is injected exactly once. */
function hash(text: string): string {
    let value = 0;
    for (let i = 0; i < text.length; i++) {
        value = (Math.imul(value, 31) + text.charCodeAt(i)) | 0;
    }
    return (value >>> 0).toString(36);
}
